/**
 * __root.tsx — app-wide providers + legacy redirect handler.
 *
 * Mounts:
 *   - <ErrorBoundary>: outermost catch for uncaught render errors
 *     (Stage 7 — essentials.md §6). Renders a minimal "copy details /
 *     reload" page. Sits OUTSIDE providers so a crash inside Clerk or
 *     QueryClient bootstrap still renders a usable screen.
 *   - QueryClientProvider: a single fresh QueryClient.
 *   - ShellProvider: cross-component sidebar state.
 *   - CommandK: global mod+k keybinding.
 *   - Legacy redirect handler: translates old `apps/dashboard` paths
 *     to the new topology.
 *
 * Also wires:
 *   - `notFoundComponent: NotFoundScreen` — every unmatched route
 *     lands on the 404 page.
 *   - `errorComponent: AppCrashScreen` — router-level render errors
 *     (inside the Outlet tree, so `ErrorBoundary` won't catch them).
 *     Receives TanStack Router's `{ error, reset }` pair.
 */

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppCrashScreen, NotFoundScreen } from '@/components/RouteError';
import { Toaster } from '@/components/Toaster';
import { CommandK } from '@/components/shell/CommandK';
import { ShellProvider } from '@/components/shell/ShellContext';
import { ClerkRootProvider } from '@/lib/clerk';
import { createQueryClient } from '@/lib/query-client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet, createRootRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect, useMemo } from 'react';

// -----------------------------------------------------------------
// Legacy redirect map (topology.md + essentials.md §2)
//
// Stage 2 deviation from topology.md: the new app runs without a
// `/app/` URL prefix (dashboard.clickweave.co already implies "app").
// All legacy URLs keep the `/app/*` shape and rewrite to the new
// un-prefixed equivalents:
//
//   legacy                     → new
//   /app/audits                → /settings/logs
//   /app/audits/:id            → /settings/logs/:id
//   /app/keys                  → /settings/keys
//   /app/members               → /settings/members
//   /app/billing               → /settings/billing      (also covers
//                                apps/api's 402 upgrade_url; essentials §2)
//   /app/sites/:siteUuid       → /origins/:siteUuid
//   /app/pages/:pageUuid       → /origins (+ ?legacy_page=... for
//                                Stage 5 toast — siteUuid lookup)
//   /app                       → /
//   /app/settings              → /settings (→ /settings/profile via
//                                the settings layout's index redirect)
//   /app/* (catch-all)         → strip the /app prefix
// -----------------------------------------------------------------

const staticRedirects: Array<{ pattern: RegExp; to: (m: RegExpMatchArray) => string }> = [
  { pattern: /^\/app\/audits\/?$/, to: () => '/settings/logs' },
  { pattern: /^\/app\/audits\/([^/]+)\/?$/, to: (m) => `/settings/logs/${m[1]}` },
  { pattern: /^\/app\/keys\/?$/, to: () => '/settings/keys' },
  { pattern: /^\/app\/members\/?$/, to: () => '/settings/members' },
  { pattern: /^\/app\/billing\/?$/, to: () => '/settings/billing' },
  { pattern: /^\/app\/sites\/([^/]+)\/?$/, to: (m) => `/origins/${m[1]}` },
];

function stripAppPrefix(pathname: string): string | null {
  if (pathname === '/app' || pathname === '/app/') return '/';
  if (pathname.startsWith('/app/')) return pathname.slice(4); // drop "/app"
  return null;
}

/**
 * Runs on every navigation and rewrites legacy URLs to the new
 * topology. Rendered once inside the root so Stage 5 routes don't
 * each have to guard against old paths.
 *
 * Note: `/app/pages/:pageUuid` needs a siteUuid lookup and is
 * special-cased. Today it lands on `/origins` with
 * `?legacy_page=...`; Stage 5 surfaces this as a toast once a page
 * cache exists.
 */
function LegacyRedirects(): null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  useEffect(() => {
    // /app/pages/:pageUuid — siteUuid-less legacy URL.
    const pageMatch = pathname.match(/^\/app\/pages\/([^/]+)\/?$/);
    if (pageMatch) {
      navigate({
        to: '/origins',
        search: { legacy_page: pageMatch[1] },
        replace: true,
      });
      return;
    }

    // Specific pattern redirects.
    for (const { pattern, to } of staticRedirects) {
      const match = pathname.match(pattern);
      if (match) {
        navigate({ to: to(match), replace: true });
        return;
      }
    }

    // Catch-all: anything still under /app/... drops the prefix.
    const stripped = stripAppPrefix(pathname);
    if (stripped !== null && stripped !== pathname) {
      navigate({ to: stripped, replace: true });
    }
  }, [pathname, navigate]);

  return null;
}

// -----------------------------------------------------------------
// Root component
// -----------------------------------------------------------------

function RootShell() {
  // Tuned `QueryClient` — caches per session, with 401/402/error
  // routing in its `QueryCache` + `MutationCache` handlers. See
  // `lib/query-client.ts`. Per-mount (not module-scoped) so HMR stays
  // predictable; the router only mounts once at boot.
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ClerkRootProvider>
          <Toaster>
            <ShellProvider>
              <LegacyRedirects />
              <CommandK />
              <Outlet />
            </ShellProvider>
          </Toaster>
        </ClerkRootProvider>
        {import.meta.env.DEV && (
          <>
            {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> */}
            {/* <TanStackRouterDevtools position="bottom-right" /> */}
          </>
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export const Route = createRootRoute({
  component: RootShell,
  notFoundComponent: NotFoundScreen,
  errorComponent: ({ error, reset }) => <AppCrashScreen error={error} reset={reset} />,
});
